import { resolveElements } from './dom'

export interface DomValueExtractOptions {
  /**
   * CSS selector
   */
  selector: string
  /**
   * If true, value will be extracted from element.innerText
   * innerText or attribute must be specified.
   */
  innerText?: boolean
  /**
   * A name of the attribute to extract value from
   * innerText or attribute must be specified.
   */
  attribute?: string
  /**
   * Optional.
   * If it is specified, the result of regex will be used as value.
   * If capture group is specified, the first group will be used.
   */
  regex?: string
}

/**
 * Given {@link DomValueExtractOptions options}, try to find matching elements
 * and return the value from the innerText or specified attribute.
 *
 * @param options
 */
export function domValueExtract (options: DomValueExtractOptions): string | null {
  if (options.selector == null || options.selector === '') {
    return null
  }

  // get a regex object if specified, we will use it later and only want to create it once
  const regex = getRegex(options.regex)
  const elements = resolveElements(options.selector)

  for (const element of elements) {
    let value = getElementValue(element, options)
    value = getValueFromRegex(value, regex)

    if (value !== '') {
      return value
    }
  }

  return null
}

/**
 * Utility to get a regular expression from a string, throw
 * if invalid, or return null if not specified.
 *
 * @param regex
 */
function getRegex (regex?: string): RegExp | null {
  if (regex == null || regex === '') {
    return null
  }

  try {
    return new RegExp(regex, 'g')
  } catch (e) {
    throw new Error('Invalid regular expression provided')
  }
}

/**
 * Given an element and {@link DomValueExtractOptions options}, return the value
 *
 * @param element
 * @param options
 */
function getElementValue (element: HTMLElement, options: DomValueExtractOptions): string {
  if (options.innerText === true) {
    return element.innerText
  } else if (options.attribute != null) {
    return element.getAttribute(options.attribute) ?? ''
  }

  return ''
}

/**
 * Given a value and a regular expression
 *  - if regex is not provided, return the value
 *  - if there are regex matches, return the first match, while attempting to return the first capture group
 *  - if there are no matches, return empty string
 *
 * @param value
 * @param regex
 */
function getValueFromRegex (value: string, regex: RegExp | null): string {
  if (regex == null) {
    return value
  }

  const matches = Array.from(value.matchAll(regex))

  if (matches.length > 0) {
    const [match, capture] = matches[0]

    // TODO: this is not the correct way to check for capture groups
    return capture ?? match
  }

  return ''
}

/**
 * Given a string or {@link DomValueExtractOptions options}, return the string itself
 * or extract the value from the DOM.
 *
 * @param option
 */
export function stringOrDomValueExtract (option: string | DomValueExtractOptions): string | null {
  if (typeof option === 'string') {
    return option
  }

  return domValueExtract(option)
}
